import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import type { AlgorithmOperation } from "@algoquest/schemas";
import { GameRuntime } from "../engine/GameRuntime";
import type { SearchViewState } from "../algorithms/searching/SearchController";
import type { SortViewState } from "../algorithms/sorting/SortController";
import type { LoadedLevel } from "../levels/levelLoader";

/**
 * The React <-> game-runtime boundary (Master Spec §6, §47). React mounts
 * exactly one <canvas>, this effect constructs exactly one GameRuntime for
 * its lifetime, and everything else talks to the runtime only through this
 * small imperative handle — React never re-renders the Three.js scene.
 *
 * Phase 05 added the Sorting (Bubble Sort) callback set alongside the
 * Phase 04 Searching callbacks; Phase 06/07 each added another
 * per-algorithm callback set the same way. Phase 08 unifies all three
 * sorting callback sets into one SortViewState-typed pair
 * (onSortViewReset/onSortViewStep), mirroring onViewReset/onViewStep —
 * GameRuntime decides which concrete algorithm fires them based on the
 * loaded level's dataset kind and algorithm; this component only wires
 * both pairs through.
 */
export interface GameCanvasHandle {
  pause: () => void;
  resume: () => void;
  reset: () => void;
  resetCamera: () => void;
  loadLevel: (level: LoadedLevel) => void;
}

export interface GameCanvasProps {
  initialLevel: LoadedLevel;
  guidedComparisons?: number;
  revealSearchValues?: boolean;
  bugDemo?: boolean;
  manualSort?: {values:number[];pair:number|null;complete:boolean};
  manualSearch?: {index:number|null;low:number;high:number;checked:number[];found:boolean};
  onNearestBoxChange?: (index: number | null) => void;
  onViewReset?: (view: SearchViewState) => void;
  onViewStep?: (view: SearchViewState, operations: AlgorithmOperation[]) => void;
  onActionRejected?: (expectedIndex: number) => void;
  onChoiceRejected?: () => void;
  onSortViewReset?: (view: SortViewState) => void;
  onSortViewStep?: (view: SortViewState, operations: AlgorithmOperation[]) => void;
  onSortActionRejected?: (expectedIndex: number) => void;
  onSortDecisionRejected?: () => void;
}

export const GameCanvas = forwardRef<GameCanvasHandle, GameCanvasProps>(function GameCanvas(
  {
    initialLevel,
    guidedComparisons,
    revealSearchValues=false,
    bugDemo=false,
    manualSearch,
    manualSort,
    onNearestBoxChange,
    onViewReset,
    onViewStep,
    onActionRejected,
    onChoiceRejected,
    onSortViewReset,
    onSortViewStep,
    onSortActionRejected,
    onSortDecisionRejected,
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const runtimeRef = useRef<GameRuntime | null>(null);

  // Runtime stays mounted; callbacks always belong to the currently selected level.
  const callbacksRef = useRef({onNearestBoxChange,onViewReset,onViewStep,onActionRejected,onChoiceRejected,onSortViewReset,onSortViewStep,onSortActionRejected,onSortDecisionRejected});
  callbacksRef.current = {onNearestBoxChange,onViewReset,onViewStep,onActionRejected,onChoiceRejected,onSortViewReset,onSortViewStep,onSortActionRejected,onSortDecisionRejected};

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const runtime = new GameRuntime(canvas, {
      onNearestBoxChange: (...args) => callbacksRef.current.onNearestBoxChange?.(...args),
      onViewReset: (...args) => callbacksRef.current.onViewReset?.(...args),
      onViewStep: (...args) => callbacksRef.current.onViewStep?.(...args),
      onActionRejected: (...args) => callbacksRef.current.onActionRejected?.(...args),
      onChoiceRejected: (...args) => callbacksRef.current.onChoiceRejected?.(...args),
      onSortViewReset: (...args) => callbacksRef.current.onSortViewReset?.(...args),
      onSortViewStep: (...args) => callbacksRef.current.onSortViewStep?.(...args),
      onSortActionRejected: (...args) => callbacksRef.current.onSortActionRejected?.(...args),
      onSortDecisionRejected: (...args) => callbacksRef.current.onSortDecisionRejected?.(...args),
    }, guidedComparisons !== undefined, revealSearchValues);
    runtime.init();
    runtime.loadLevel(initialLevel);
    runtime.setGuidedComparisons(guidedComparisons??0);
    runtime.setBugDemo(bugDemo);
    if(manualSearch)runtime.setManualSearch(manualSearch);
    if(manualSort)runtime.setManualSort(manualSort);
    runtime.start();
    runtimeRef.current = runtime;

    return () => {
      runtime.dispose();
      runtimeRef.current = null;
    };
    // The runtime is constructed once; the callback bridge above stays current.
    // Level switching (including switching between algorithms and
    // between the Searching/Sorting stages) goes through the imperative
    // `loadLevel` handle below, not through re-running this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(()=>{if(manualSort)runtimeRef.current?.setManualSort(manualSort);},[manualSort]);
  useEffect(()=>{if(manualSearch)runtimeRef.current?.setManualSearch(manualSearch);},[manualSearch]);
  useEffect(()=>{runtimeRef.current?.setBugDemo(bugDemo);},[bugDemo]);
  useEffect(()=>{runtimeRef.current?.setGuidedComparisons(guidedComparisons??0);},[guidedComparisons]);

  useImperativeHandle(
    ref,
    () => ({
      pause: () => runtimeRef.current?.pause(),
      resume: () => runtimeRef.current?.resume(),
      reset: () => runtimeRef.current?.reset(),
      resetCamera: () => runtimeRef.current?.resetCamera(),
      loadLevel: (level) => runtimeRef.current?.loadLevel(level),
    }),
    [],
  );

  return <canvas ref={canvasRef} className="block h-full w-full" />;
});
